import { CheckIcon } from '@heroicons/react/24/outline';
import { Button, FormControl, FormLabel, Radio, RadioGroup } from '@mui/joy';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { TraceTTLTier } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { PAYMENT_PLAN_PRICING_MAP } from '../utils/constants';
import { useConfigureOrgTTLSettings } from '../utils/ttlSettings.util';

const DataRetentionSetting = () => {
  const { tier, isEnterprise } = useCurrentTier();
  const { shortLivedPricePerTrace, longLivedPricePerTrace } =
    tier && PAYMENT_PLAN_PRICING_MAP[tier]
      ? PAYMENT_PLAN_PRICING_MAP[tier]
      : PAYMENT_PLAN_PRICING_MAP['plus'];

  const ttlDurations = useInstanceFlagOrDefault(
    InstanceFlags.trace_tier_duration_days
  );

  const { isLoading, allTTLSettings, handleOrgTTLChange } =
    useConfigureOrgTTLSettings();
  const orgTTLSetting = allTTLSettings?.find((t) => t.tenant_id == null);
  const [ttl, setTTL] = useState<TraceTTLTier | undefined>(
    orgTTLSetting?.default_trace_tier
  );

  useEffect(() => {
    if (orgTTLSetting?.default_trace_tier) {
      setTTL(orgTTLSetting?.default_trace_tier);
    }
  }, [orgTTLSetting?.default_trace_tier]);

  const showSaveButton =
    !isLoading && ttl !== orgTTLSetting?.default_trace_tier;
  if (isLoading) {
    return null;
  }

  return (
    <div className="relative flex flex-col items-stretch gap-4">
      {showSaveButton && (
        <div className={cn('flex gap-2', 'absolute right-0 top-0')}>
          <Button
            color="neutral"
            variant="outlined"
            size="sm"
            onClick={() => {
              setTTL(orgTTLSetting?.default_trace_tier);
            }}
          >
            Cancel
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm">Save</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  if (ttl) {
                    handleOrgTTLChange(ttl, true);
                  }
                }}
              >
                Apply to all existing projects
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  if (ttl) {
                    handleOrgTTLChange(ttl, false);
                  }
                }}
              >
                Apply to new projects
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      <div className="text-lg font-semibold">
        Organization data retention policy
      </div>
      <RadioGroup
        value={ttl}
        onChange={(e) => {
          setTTL(e.target.value as TraceTTLTier);
        }}
        name="radio-buttons-group"
      >
        <FormControl
          sx={{ flexDirection: 'row' }}
          className="mb-4 gap-4 rounded-lg border border-secondary p-6"
        >
          <Radio value={TraceTTLTier.shortlived} className="mt-[2px]" />
          <div className="flex flex-col gap-2">
            <FormLabel
              className="text-sm font-semibold text-ls-black"
              sx={{ fontWeight: 700 }}
            >
              Base{' '}
              <div className="ml-2 rounded-md bg-tertiary px-2 py-1 text-xs">
                DEFAULT
              </div>
            </FormLabel>
            <div className="flex flex-col items-start justify-start gap-2 self-stretch text-sm font-medium leading-snug">
              Delete traces after {ttlDurations[TraceTTLTier.shortlived]} days
              by default.
            </div>
            <div className="flex flex-col items-start justify-start gap-2 self-stretch text-sm font-normal leading-snug">
              <div className="flex gap-2">
                {!isEnterprise && <CheckIcon className="h-4 w-4 flex-none" />}
                <div>
                  <b>Some traces may auto-upgrade to extended data retention</b>{' '}
                  if interacted with by specific features. See the complete list{' '}
                  <Link
                    to="https://docs.langchain.com/langsmith/administration-overview#data-retention"
                    target="_blank"
                  >
                    <span className="underline">here</span>
                  </Link>
                </div>
              </div>
              {!isEnterprise && (
                <div className="flex gap-2">
                  <CheckIcon className="h-4 w-4 flex-none" />
                  Calculated at ${shortLivedPricePerTrace} per base retention
                  trace <br></br>and an additional ${longLivedPricePerTrace} per
                  upgrade
                </div>
              )}
            </div>
            <div className="text-sm font-semibold text-ls-black"></div>
          </div>
        </FormControl>
        <FormControl
          sx={{ flexDirection: 'row' }}
          className="gap-4 rounded-lg border border-secondary p-6"
        >
          <Radio value={TraceTTLTier.longlived} className="mt-[2px]" />
          <div className="flex flex-col gap-2">
            <FormLabel
              className="text-sm font-bold text-ls-black"
              sx={{ fontWeight: 700 }}
            >
              Extended
            </FormLabel>
            <div className="flex flex-col items-start justify-start gap-2 self-stretch text-sm font-medium leading-snug">
              All traces are retained for {ttlDurations[TraceTTLTier.longlived]}{' '}
              days.
            </div>
            {!isEnterprise && (
              <div className="flex flex-col items-start justify-start gap-2 self-stretch text-sm font-normal leading-snug">
                <div className="flex gap-2">
                  <CheckIcon className="h-4 w-4 flex-none" />
                  All traces calculated at $
                  {(longLivedPricePerTrace + shortLivedPricePerTrace).toFixed(
                    4
                  )}{' '}
                  per trace
                </div>
              </div>
            )}
          </div>
        </FormControl>
      </RadioGroup>
    </div>
  );
};

export { DataRetentionSetting };
